import React, { useContext, useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ajax } from 'jquery';

import { Context } from './StockManagerContext.jsx';
import { Form, InputGroup, Spinner, Table, Accordion, Card, Button } from 'react-bootstrap';
import { canSeeCustomerDetails, shouldRecordPayments } from '../private/permissions.js';
import IconTrash from './IconTrash.jsx';
import { formatMoney, cachedGet } from './utils.js';

const post = function (url, body) {
    return ajax({
        type: 'POST',
        url: url,
        data: JSON.stringify(body),
        contentType: "application/json",
        dataType: 'json'
    });
}

export default function ShipmentsModal({ isActive, onHide }) {

    const { orders, currentCustomer, resetOrders, currentUser, deleteOrder, upsellData, payments, setPayments, paymentMethods } = useContext(Context);

    const [isSaving, setIsSaving] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalDiscounts, setTotalDiscounts] = useState(0);
    const [isSkinny, setIsSkinny] = useState(false);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('1');

    const [isSplitPayment, setIsSplitPayment] = useState(false);

    // Commission split - order-level, applied to every order's opportunity.
    // Up to MAX_SPLITS people; each row reveals the next once a person is chosen.
    // Capped at 3 to match the commission split fields that exist in Zoho.
    const MAX_SPLITS = 3;
    const [users, setUsers] = useState([]);
    const [splits, setSplits] = useState([{ with: '', percentage: '' }]);

    useEffect(() => {
        cachedGet('/api/zoho/users').then(setUsers);
    }, []);

    const sortedUsers = useMemo(
        () => [...users].sort((a, b) => a.full_name.localeCompare(b.full_name)),
        [users]
    );

    // How many split rows to show: row 0 always, each later row appears once the
    // previous row has a person selected (capped at MAX_SPLITS).
    let visibleSplits = 1;
    while (visibleSplits < MAX_SPLITS && splits[visibleSplits - 1]?.with) {
        visibleSplits++;
    }

    const setSplitField = (i, field, value) => {
        setSplits(prev => {
            const next = prev.map(s => ({ ...s }));
            while (next.length <= i) next.push({ with: '', percentage: '' });
            next[i][field] = value;
            // Clearing a person empties its percentage and collapses later rows
            if (field === 'with' && !value) {
                next[i].percentage = '';
                for (let j = i + 1; j < next.length; j++) {
                    next[j] = { with: '', percentage: '' };
                }
            }
            return next;
        });
    };

    const handleSave = async () => {
        setIsSaving(true);

        const email = currentCustomer.Email;
        const customerId = currentCustomer.id;

        try {
            // Add payments to each order
            const ordersWithPayments = orders.map((order, idx) => {
                return {
                    ...order,
                    payments: paymentsByOrder[idx] || []
                };
            });

            const body = {email, orders: ordersWithPayments, payments, customerId};
            // Send only the splits that have a person chosen, in order (max MAX_SPLITS)
            const commissionSplits = splits
                .filter(s => s.with)
                .slice(0, MAX_SPLITS)
                .map(s => ({ with: s.with, percentage: parseInt(s.percentage, 10) || 0 }));
            if (commissionSplits.length) {
                body.commissionSplits = commissionSplits;
            }

            const response = await post('/api/orders', body);
            console.log('Create order response', response);

            if (!response || response.status !== 'success') {
                alert('An error occurred creating this order. Contact Neto Support Team and provide the following information:\n\n' + response?.responses?.map(r => JSON.stringify(r?.error)).join('\n'));
                setIsSaving(false);
                return;
            }

            console.log('Response', response);

            // Clean up the UI
            resetOrders();
            setIsSaving(false);
            onHide();

            // Side effect - reload the datatable if it's present
            window.reload_table && window.reload_table();

        } catch (error) {
            alert('An error occurred creating this order. Contact Neto Support Team and provide the following information:\n\n' + JSON.stringify(error));
            setIsSaving(false);
            return;
        }


    };

    useEffect(() => {
        // Set the payment method to the key of the payment method named "Direct Deposit"
        const directDepositEntry = Object.entries(paymentMethods).find(([key, label]) => label.toLowerCase() === 'direct deposit');
        if ( directDepositEntry ) {
            console.log('Setting default payment method to Direct Deposit', directDepositEntry[0]);
            setDefaultPaymentMethod(directDepositEntry[0]);
        }

    }, [paymentMethods])

    useEffect(() => {
        let totalDiscounts = orders.reduce((sum, order) => {
            return sum + order.products.reduce((discSum, product) => discSum + (product.discount || 0), 0);
        }, 0);

        const total = orders.reduce((sum, order) => {
            const productSubtotal = order.products.reduce((prodSum, product) => {
                const price = parseFloat(product.price) || 0;
                return prodSum + (price * (product.qty||1));
            }, 0);

            const selectedUpsellSubtotal = Object.values(order.selectedUpsells).reduce((upsellSum, upsells) => {
                return upsellSum + Object.entries(upsells).reduce((upsellInnerSum, [sku, {qty, discount, price}]) => {
                    if (qty <= 0) {
                        return upsellInnerSum;
                    }

                    totalDiscounts += discount;
                    return upsellInnerSum + (price * qty);
                }, 0);
            }, 0);

            return sum + productSubtotal + selectedUpsellSubtotal;
        }, 0);

        setTotal(total);
        setTotalDiscounts(totalDiscounts);

        setIsSkinny(orders.some(order => order.orderData.isSkinny));

        // If we have a single payment, update its amount to match total deposit
        if ( payments.length === 1 ) {
            const totalDeposit = orders.reduce((sum, order) => sum + (order.orderData.deposit || 0), 0);
            setPayments([{ method: payments[0].method, amount: totalDeposit }]);
        }

    }, [orders, upsellData]);

    // TODO - Turn this into state?
    const totalDeposit = orders.reduce((sum, order) => sum + (order.orderData.deposit || 0), 0);

    // Distribute payment amounts across deposits
    const [paymentsByOrder, setPaymentsByOrder] = useState([]);
    useEffect(() => {

        const paymentsCopy = payments.map(p => ({ ...p }));
        const paymentsByOrder = orders.map(order => {
            const orderDeposit = order.orderData.deposit || 0;
            let remainingDeposit = orderDeposit;

            const orderPayments = [];

            for ( const payment of paymentsCopy ) {
                if ( remainingDeposit <= 0 ) {
                    break;
                }

                const paymentAmount = Math.min(payment.amount, remainingDeposit);
                if ( paymentAmount > 0 ) {
                    orderPayments.push({
                        order: order.products.map(p => p.sku).join(', '),
                        // Zoho stores the human-readable method, not Neto's internal
                        // PaymentMethodID used by this select control.
                        method: paymentMethods[payment.method] || payment.method,
                        amount: paymentAmount
                    });
                    remainingDeposit -= paymentAmount;
                    payment.amount -= paymentAmount;
                }
            }

            return orderPayments;
        });

        setPaymentsByOrder(paymentsByOrder);

    }, [payments, orders, paymentMethods]);

    const paymentDiff = totalDeposit - payments.reduce((sum, p) => sum + p.amount, 0);
    const hasPaymentVariance = shouldRecordPayments(currentUser.userRole) && Math.abs(payments.reduce((sum, p) => sum + p.amount, 0) - totalDeposit) > 0.01;

    return <Modal show={isActive} size="xl" onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>Your Quote</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <Table striped bordered hover size="sm">
                <thead>
                    <tr>
                        <th>Shipping Address</th>
                        <th>Date Required</th>
                        <th>Warehouse</th>
                        <th>Products</th>
                        <th style={{width: 0, minWidth: 150}}>Subtotal</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.length ? orders.map((order, idx) => {
                        const firstProduct = order.products[0];
                        const addr = order.orderData.shippingAddress || {};
                        const addressString = [addr.firstName, addr.lastName, addr.street1, addr.city, addr.postcode].filter(Boolean).join(', ') || '-';

                        return (
                            <tr key={idx}>
                                <td>{addressString}</td>
                                <td>{order.orderData.dateRequired || '-'}</td>
                                <td>{order.orderData.warehouseCode || firstProduct.warehouseCode || '-'}</td>
                                <td>
                                    {order.products.map(product => product.sku).join(', ') || '-'}
                                    {order.orderData.isSkinny && <div><small className="badge badge-warning">Skinny Expires {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB')}</small></div>}
                                </td>
                                <td className="text-right">{formatMoney(order.orderData.calculatedTotal)}</td>
                                <td><button className="btn btn-primary btn-sm" onClick={() => {
                                        window.OrderForm.openToOrder(idx);
                                    }}>Edit</button>
                                    <button className="btn btn-danger btn-sm ml-1" onClick={() => {
                                        // Release hold on products in this order
                                        order.products.forEach(product => {
                                            if (product.enableQty) {
                                                // Don't hold stock for virtual items
                                                return;
                                            }
                                            post('/api/row-action', { sku: product.sku, action: 'release_hold' });
                                        });

                                        deleteOrder(idx);
                                    }}><IconTrash size={15} /></button>
                                </td>
                            </tr>
                        );
                    }) : <tr><td colSpan="6" className="text-center">No current orders</td></tr>}
                </tbody>
                <tfoot>
                    {totalDiscounts > 0 && <><tr>
                        <th colSpan="4" className="text-right border-0 font-weight-normal">Order Total (inc GST)</th>
                        <th className="text-right font-weight-normal">{formatMoney(total)}</th>
                    </tr>
                    <tr>
                        <th colSpan="4" className="text-right border-0 font-weight-normal">Discount</th>
                        <th className="text-right font-weight-normal">{formatMoney(totalDiscounts)}</th>
                    </tr>
                    </>}
                    <tr>
                        <th colSpan="4" className="text-right border-0">Total (inc GST)</th>
                        <th className="text-right">{formatMoney(total - totalDiscounts)}</th>
                    </tr>

                    <tr>
                        <th colSpan="4" className="text-right border-0">Deposit Paid Today</th>
                        <th className="text-right">
                            {formatMoney(totalDeposit)}

                        </th>
                        <th>
                            {isSkinny && <span className="badge badge-warning ml-2">Includes Skinny Deposit</span>}
                        </th>
                    </tr>
                    {shouldRecordPayments(currentUser.userRole) && paymentMethods && Object.keys(paymentMethods).length > 0 && <tr>
                        <th colSpan="4" className="text-right border-0">Payment Method</th>
                        <th colSpan="2" className="text-right">
                            <select className="form-control form-control-sm" onChange={e => {
                                if ( e.target.value !== 'split' ) {
                                    setIsSplitPayment(false);
                                    setPayments([{ method: e.target.value, amount: totalDeposit }]);
                                } else {
                                    setIsSplitPayment(true);
                                    setPayments([
                                        { method: payments[0].method, amount: totalDeposit },
                                        { method: defaultPaymentMethod, amount: 0 },
                                    ]);
                                }
                            }} defaultValue={isSplitPayment ? 'split' : payments[0]?.method}>
                                <option>--- Select Payment Method (required) ---</option>
                                {Object.entries(paymentMethods).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                                <option value="split">⋔ Split Payments</option>
                            </select>
                        </th>
                    </tr>}
                    {isSplitPayment && <tr>
                        <th colSpan="1" className="text-right border-0"></th>
                        <td colSpan="5" className="text-center p-3">
                            <h6>Split Payments</h6>
                            {payments.map((payment, pIdx) => (
                                <div key={pIdx} style={{ marginBottom: 8 }} className="form form-inline">
                                    <select className="form-control" style={{ flexGrow: 1, marginRight: 8 }} value={payment.method} onChange={e => {
                                        const newPayments = [...payments];
                                        newPayments[pIdx].method = e.target.value;
                                        setPayments(newPayments);
                                    }}>
                                        {Object.entries(paymentMethods).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                    <InputGroup>
                                        <InputGroup.Prepend>
                                            <InputGroup.Text>$</InputGroup.Text>
                                        </InputGroup.Prepend>
                                        <Form.Control type="number" min="0" style={{ flexGrow: 1 }} value={payment.amount.toString() || ''} onChange={e => {
                                            const newPayments = [...payments];
                                            newPayments[pIdx].amount = parseFloat(e.target.valueAsNumber) || 0.00;
                                            setPayments(newPayments);
                                        }} />
                                    </InputGroup>
                                    {payments.length > 1 && <button className="btn btn-danger btn-sm ml-2" onClick={() => {
                                        const newPayments = payments.filter((_, idx) => idx !== pIdx);
                                        setPayments(newPayments);
                                    }}><IconTrash size={12} /></button>}
                                </div>
                            ))}
                            <button className="btn btn-outline btn-sm" onClick={() => {
                                setPayments([...payments, { method: defaultPaymentMethod, amount: paymentDiff > 0 ? parseFloat(paymentDiff.toFixed(2)) : 0 }]);
                            }}>+ Add Payment</button>
                            {hasPaymentVariance && <p><span className="badge badge-warning">Total is out by {formatMoney(totalDeposit - payments.reduce((sum, p) => sum + p.amount, 0))}</span></p>}
                        </td>
                    </tr>}
                </tfoot>
            </Table>

            </Modal.Body>
            <Modal.Footer>
                <div className="mr-auto">
                    {Array.from({ length: visibleSplits }).map((_, i) => {
                        const split = splits[i] || { with: '', percentage: '' };
                        return (
                            <div key={i} className="form-inline mb-1">
                                <select className="form-control form-control-sm" value={split.with} onChange={e => setSplitField(i, 'with', e.target.value)}>
                                    <option value="">{i === 0 ? 'Commission Split' : '--- Add another split ---'}</option>
                                    {sortedUsers.map(user => (
                                        <option key={user.id} value={user.id}>{user.full_name}</option>
                                    ))}
                                </select>
                                {split.with && <InputGroup size="sm" className="ml-2" style={{ width: 110 }}>
                                    <Form.Control type="number" min="1" max="100" step="1" placeholder="0" value={split.percentage} onChange={e => setSplitField(i, 'percentage', e.target.value)} />
                                    <InputGroup.Append>
                                        <InputGroup.Text>%</InputGroup.Text>
                                    </InputGroup.Append>
                                </InputGroup>}
                            </div>
                        );
                    })}
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => {onHide()}}>Close</button>
                <button className="btn btn-primary btn-sm" onClick={() => {handleSave()}} disabled={isSaving || orders.length === 0 || hasPaymentVariance}>
                    {isSaving ? <><Spinner
                        style={{ marginRight: '5px' }}
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        />
                        Sending to Neto...</> : `Finalise and send to Neto`}</button>

                {__DEBUG_WITH_DEFAULT_CUSTOMER__ && <div style={{ flexBasis: '100%' }}>
                    <Accordion>
                        <Card>
                            <Accordion.Toggle as={Card.Header} variant="link" eventKey="0">
                                Orders
                            </Accordion.Toggle>

                            <Accordion.Collapse eventKey="0">
                            <Card.Body>
                                <pre>
                                    {JSON.stringify(orders.map((order, idx) => {
                                        // This is ordersWithPayments
                                        return {
                                            ...order,
                                            payments: paymentsByOrder[idx] || []
                                        };
                                    }), null, 2)}
                                </pre>

                            </Card.Body>
                            </Accordion.Collapse>
                        </Card>
                        <Card>
                            <Accordion.Toggle as={Card.Header} variant="link" eventKey="1">
                                Payments
                            </Accordion.Toggle>

                            <Accordion.Collapse eventKey="1">
                            <Card.Body>
                                <Table>
                                    <tbody>
                                        {paymentsByOrder.map((orderPayments, idx) => <tr key={idx}>
                                            <td>{orders[idx] && orders[idx].products.map(product => product.sku).join(', ')}</td>
                                            <td>
                                                <Table size="sm" bordered>
                                                    <thead>
                                                        <tr>
                                                            <th>Method</th>
                                                            <th>Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {orderPayments.map((payment, pIdx) => (
                                                            <tr key={pIdx}>
                                                                <td>{paymentMethods[payment.method] || payment.method}</td>
                                                                <td className="text-right">{formatMoney(payment.amount)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </td>
                                        </tr>)}
                                    </tbody>
                                </Table>

                            </Card.Body>
                            </Accordion.Collapse>
                        </Card>
                    </Accordion>

                    </div>}
            </Modal.Footer>
        </Modal>
}
